import type { CardBlockConfig } from '~~/layers/base/types/blockConfigs'
import {
    CHANGE_PASSWORD_REQUIREMENTS_MESSAGE,
    CHANGE_PASSWORD_REQUIREMENTS_TITLE,
} from '~~/layers/base/utils/passwordPolicy'

/** HMAlert-sized UCard body pad (14px / 18px → rem). Pass as CardBlock `:ui`. */
export const AUTH_ALERT_CARD_BODY_UI = {
    body: '!px-4.5 !py-3.5',
} as const

export type AuthAlertVariant = Extract<
    NonNullable<CardBlockConfig['variant']>,
    'info' | 'error' | 'success'
>

export type AuthAlertCardOptions = {
    id: string
    variant: AuthAlertVariant
    title: string
    /** When omitted, renders a title-only alert (e.g. single-line API errors). */
    body?: string
    icon?: string | false
}

export type AuthErrorMessageParts = {
    title: string
    body?: string
}

/**
 * Builds a title (+ optional body) CardBlock for auth alerts.
 * Variant chrome (colors, border, default icons) comes from CardBlock.
 */
export function getAuthAlertCardConfig(opts: AuthAlertCardOptions): CardBlockConfig {
    const content: CardBlockConfig['content'] = [
        {
            id: `${opts.id}-title`,
            type: 'text',
            text: opts.title,
            ui: opts.body
                ? 'text-md font-semibold leading-[1.3] mb-0.75'
                : 'text-md font-semibold leading-[1.3]',
        },
    ]
    if (opts.body) {
        content.push({
            id: `${opts.id}-body`,
            type: 'text',
            text: opts.body,
            ui: 'text-md leading-normal',
        })
    }
    return {
        id: opts.id,
        type: 'card',
        variant: opts.variant,
        ...(opts.icon !== undefined ? { icon: opts.icon } : {}),
        content,
    }
}

/**
 * Splits known API error copy into alert title + body. Password-complexity failures
 * from `auth.ts` return only the requirements body; UI adds the invalid title.
 */
export function splitAuthErrorMessage(errorMessage: string): AuthErrorMessageParts {
    if (errorMessage === CHANGE_PASSWORD_REQUIREMENTS_MESSAGE) {
        return {
            title: CHANGE_PASSWORD_REQUIREMENTS_TITLE,
            body: CHANGE_PASSWORD_REQUIREMENTS_MESSAGE,
        }
    }
    return { title: errorMessage }
}

/**
 * Returns a CardBlockConfig for displaying an authentication error message.
 * Password-complexity copy gets a bold “Password is not valid” header + regular body.
 */
export const getAuthErrorConfig = (errorMessage: string): CardBlockConfig => {
    const { title, body } = splitAuthErrorMessage(errorMessage)
    return getAuthAlertCardConfig({
        id: 'auth-error',
        variant: 'error',
        title,
        body,
    })
}
